/**
 * Single source of truth for **customer-facing** plan features:
 * homepage pricing cards, feature comparison table, billing page bullets,
 * and Settings plan summaries (via entitlements.featureBullets).
 *
 * Rules:
 * - Only list features that **ship today** in production.
 * - Do NOT add "coming soon", roadmap, or unreleased integrations here.
 * - When you ship a tier-gated feature, update this file AND entitlements gates.
 * - Competitor pages (`lib/marketing/compare-pages.ts`) must only claim Jorvio
 *   features that appear here or in entitlements.
 *
 * See `.cursor/rules/jorvio-pricing-marketing-truth.mdc`
 */

import type { TierPriceDisplay } from "@/lib/platform-pricing-sale";
import { PLATFORM_TRIAL_DAYS } from "@/lib/platform-trial-days";
import { formatPriceUsdMonthly } from "@/lib/pricing-public";

export type PlanTier = "starter" | "solo" | "pro" | "agency";

export type MarketingComparisonCell = { kind: "text"; value: string } | { kind: "yes" };

export type MarketingComparisonRow = {
  feature: string;
  starter: MarketingComparisonCell;
  solo: MarketingComparisonCell;
  pro: MarketingComparisonCell;
  agency: MarketingComparisonCell;
};

/** Tier card bullet lists (homepage + billing plan cards). */
export const MARKETING_TIER_CARD_FEATURES: Record<PlanTier, readonly string[]> = {
  starter: [
    "Unlimited sent quotes (email, PDF, or link)",
    "1 owner account",
    "AI quote drafting & assistant — 3 actions/day",
    "Options quotes (offer multiple choices in one quote)",
    "Customer portal, PDFs, templates, calendar & jobs",
    "In-app customer messaging",
    "1-step follow-up automation & scheduled send",
    "Cost & profit tracking, change orders & photo upload",
    "Material calculator + your-records vs customer-view markup",
    "Google review request emails",
  ],
  solo: [
    "Unlimited sent quotes (email, PDF, or link)",
    "1 owner account",
    "AI quote drafting & assistant — 5 actions/day",
    "Customer portal, PDFs, Stripe checkout, templates, calendar & jobs",
    "In-app customer messaging",
    "1-step follow-up automation & scheduled send",
    "Cost & profit tracking, deposits, change orders & photo upload",
    "Google review request emails after payment",
    "Everything in Starter",
  ],
  pro: [
    "Unlimited sent quotes (email, PDF, or link)",
    "1 owner + 2 team members (add more at $15/seat, up to 5 extras)",
    "Up to 10 crew or subcontractor calendar seats",
    "Unlimited AI drafting, assistant & review (no daily limit)",
    "Voice & photo capture + live material price finder (buy links & store discounts)",
    "AI quote review (pricing, scope, terms, positioning)",
    "Follow-up automation + analytics (2 steps)",
    "Team activity audit",
    "Everything in Solo",
  ],
  agency: [
    "Unlimited sent quotes (email, PDF, or link)",
    "1 owner + 10 team members (add more at $15/seat, no cap)",
    "Unlimited crew or subcontractor calendar seats",
    "Your brand on portal & quotes",
    "3-step follow-up automation + AI quote coach",
    "Priority support",
    "Everything in Pro",
  ],
} as const;

/** Static feature rows (price row appended at runtime). */
export const MARKETING_FEATURE_COMPARISON_ROWS: MarketingComparisonRow[] = [
  {
    feature: "Sent quotes (email, PDF, or link)",
    starter: { kind: "text", value: "Unlimited" },
    solo: { kind: "text", value: "Unlimited" },
    pro: { kind: "text", value: "Unlimited" },
    agency: { kind: "text", value: "Unlimited" },
  },
  {
    feature: "Options quotes (multiple choices in one quote)",
    starter: { kind: "yes" },
    solo: { kind: "yes" },
    pro: { kind: "yes" },
    agency: { kind: "yes" },
  },
  {
    feature: "Full-access users (owner + team)",
    starter: { kind: "text", value: "1 owner" },
    solo: { kind: "text", value: "1 owner" },
    pro: { kind: "text", value: "1 owner + 2 (up to 5 extras at $15/seat)" },
    agency: { kind: "text", value: "1 owner + 10 (extras at $15/seat, no cap)" },
  },
  {
    feature: "Crew or subcontractor calendar seats (view-only)",
    starter: { kind: "text", value: "—" },
    solo: { kind: "text", value: "—" },
    pro: { kind: "text", value: "Up to 10" },
    agency: { kind: "text", value: "Unlimited" },
  },
  { feature: "Cost & profit tracking", starter: { kind: "yes" }, solo: { kind: "yes" }, pro: { kind: "yes" }, agency: { kind: "yes" } },
  { feature: "Markup with your-records vs customer view", starter: { kind: "yes" }, solo: { kind: "yes" }, pro: { kind: "yes" }, agency: { kind: "yes" } },
  { feature: "Material calculator (quick math → line item)", starter: { kind: "yes" }, solo: { kind: "yes" }, pro: { kind: "yes" }, agency: { kind: "yes" } },
  { feature: "Decision mode for customers", starter: { kind: "yes" }, solo: { kind: "yes" }, pro: { kind: "yes" }, agency: { kind: "yes" } },
  { feature: "Photo upload & plan markup", starter: { kind: "yes" }, solo: { kind: "yes" }, pro: { kind: "yes" }, agency: { kind: "yes" } },
  { feature: "Subcontractor directory", starter: { kind: "yes" }, solo: { kind: "yes" }, pro: { kind: "yes" }, agency: { kind: "yes" } },
  { feature: "Add-ons & change orders", starter: { kind: "yes" }, solo: { kind: "yes" }, pro: { kind: "yes" }, agency: { kind: "yes" } },
  {
    feature: "Customer portal & view tracking",
    starter: { kind: "yes" },
    solo: { kind: "yes" },
    pro: { kind: "yes" },
    agency: { kind: "yes" },
  },
  {
    feature: "In-app customer messaging",
    starter: { kind: "yes" },
    solo: { kind: "yes" },
    pro: { kind: "yes" },
    agency: { kind: "yes" },
  },
  {
    feature: "Scheduled quote send",
    starter: { kind: "yes" },
    solo: { kind: "yes" },
    pro: { kind: "yes" },
    agency: { kind: "yes" },
  },
  {
    feature: "Google review request emails",
    starter: { kind: "yes" },
    solo: { kind: "yes" },
    pro: { kind: "yes" },
    agency: { kind: "yes" },
  },
  {
    feature: "Deposit & payment checkout (Stripe Connect)",
    starter: { kind: "text", value: "—" },
    solo: { kind: "yes" },
    pro: { kind: "yes" },
    agency: { kind: "yes" },
  },
  {
    feature: "Customer support",
    starter: { kind: "text", value: "Standard" },
    solo: { kind: "text", value: "Standard" },
    pro: { kind: "text", value: "Standard" },
    agency: { kind: "text", value: "Priority" },
  },
  {
    feature: "AI quote drafting, assistant & review (shared daily limit)",
    starter: { kind: "text", value: "3 AI uses/day" },
    solo: { kind: "text", value: "5 AI uses/day" },
    pro: { kind: "text", value: "Unlimited" },
    agency: { kind: "text", value: "Unlimited" },
  },
  {
    feature: "Live material price finder — buy links & store discounts (Find material)",
    starter: { kind: "text", value: "—" },
    solo: { kind: "text", value: "—" },
    pro: { kind: "yes" },
    agency: { kind: "yes" },
  },
  {
    feature: "Voice + photo notes capture",
    starter: { kind: "text", value: "—" },
    solo: { kind: "text", value: "—" },
    pro: { kind: "yes" },
    agency: { kind: "yes" },
  },
  {
    feature: "Follow-up automation",
    starter: { kind: "text", value: "1 step" },
    solo: { kind: "text", value: "1 step" },
    pro: { kind: "text", value: "2 steps + analytics" },
    agency: { kind: "text", value: "3 steps + analytics + AI coach" },
  },
  { feature: "Jobs linked to quotes", starter: { kind: "yes" }, solo: { kind: "yes" }, pro: { kind: "yes" }, agency: { kind: "yes" } },
  {
    feature: "Crew or subcontractor scheduling & calendar",
    starter: { kind: "text", value: "—" },
    solo: { kind: "text", value: "—" },
    pro: { kind: "yes" },
    agency: { kind: "yes" },
  },
  {
    feature: "Team activity audit (who changed what)",
    starter: { kind: "text", value: "—" },
    solo: { kind: "text", value: "—" },
    pro: { kind: "yes" },
    agency: { kind: "yes" },
  },
  {
    feature: "Custom branding",
    starter: { kind: "text", value: "Jorvio branding" },
    solo: { kind: "text", value: "Jorvio branding" },
    pro: { kind: "text", value: "Jorvio branding" },
    agency: { kind: "text", value: "Your brand" },
  },
];

export type MarketingPriceRowInput = {
  isAnnual: boolean;
  monthly: { starter: TierPriceDisplay; solo: TierPriceDisplay; pro: TierPriceDisplay; agency: TierPriceDisplay };
  annual: {
    starter: { total: number; perMonth: number };
    solo: { total: number; perMonth: number };
    pro: { total: number; perMonth: number };
    agency: { total: number; perMonth: number };
  };
};

function priceCell(m: TierPriceDisplay, a: { total: number; perMonth: number }, isAnnual: boolean): MarketingComparisonCell {
  if (isAnnual) {
    return { kind: "text", value: `$${a.perMonth.toFixed(2)}/mo, billed $${a.total}/yr` };
  }
  return m.saleActive && m.salePriceLabel
    ? { kind: "text", value: `${m.salePriceLabel}/mo (was ${m.listPriceLabel})` }
    : { kind: "text", value: `${formatPriceUsdMonthly(m.listPrice)}/mo` };
}

/** Full homepage comparison table including dynamic price row. */
export function buildMarketingFeatureComparisonRows(input: MarketingPriceRowInput): MarketingComparisonRow[] {
  const { isAnnual, monthly, annual } = input;
  return [
    {
      feature: isAnnual ? "Annual price (billed once)" : "Monthly price",
      starter: priceCell(monthly.starter, annual.starter, isAnnual),
      solo: priceCell(monthly.solo, annual.solo, isAnnual),
      pro: priceCell(monthly.pro, annual.pro, isAnnual),
      agency: priceCell(monthly.agency, annual.agency, isAnnual),
    },
    ...MARKETING_FEATURE_COMPARISON_ROWS,
  ];
}

/** Shipped trial copy for marketing / compare pages (matches STRIPE_TRIAL_* default). */
export const MARKETING_SOLO_TRIAL_LABEL = `${PLATFORM_TRIAL_DAYS}-day free trial, no card`;
